import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Calendar, CheckCircle2, type LucideIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { getServiceById, services } from '@/services/serviceData';
import { Service } from '@/types/services';
import { Helmet } from 'react-helmet-async';
import ServiceCard, { serviceImage, serviceStack } from '@/components/services/ServiceCard';
import { getCategoryLabel } from '@/components/services/ServiceCategories';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

import { CALENDLY_URL } from '@/lib/site';
import { serviceSeo } from '@/services/serviceSeo';



const crossLinks: Record<string, { title: string; body: string; to: string; cta: string }> = {
  'seo-analytics': {
    title: 'Explore our small business SEO services',
    body: 'How we help your business rank higher, get more leads and grow with a friendly, data-driven SEO approach.',
    to: '/seo/seo-services',
    cta: 'See SEO services',
  },
  'web-software-development': {
    title: 'Our website design philosophy',
    body: 'Why we hand-code every website for speed, security and SEO. No page builders or WordPress.',
    to: '/software/webdesign',
    cta: 'Explore website design',
  },
};

const ServiceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const service = getServiceById(id || '');

  if (!service) {
    return (
      <div className="container-custom flex min-h-screen flex-col items-center justify-center pt-16 text-center">
        <h1 className="text-3xl font-bold">Service Not Found</h1>
        <p className="mt-3 text-slate-600 dark:text-slate-400">Sorry, the service you're looking for doesn't exist.</p>
        <Button asChild className="mt-6 rounded-full">
          <Link to="/services">Back to Services</Link>
        </Button>
      </div>
    );
  }

  const IconComponent = LucideIcons[service.icon as keyof typeof LucideIcons] as LucideIcon | undefined;
  const stack = serviceStack(service);
  const related = services.filter(s => s.category === service.category && s.id !== service.id).slice(0, 3);
  const crossLink = crossLinks[service.id];
  const faqs = [
    {
      q: `What does your ${service.title} include?`,
      a: `Our ${service.title} covers everything you need to succeed, from initial consultation to ongoing support. Contact us for a detailed breakdown.`,
    },
    {
      q: 'How is your approach different for US small businesses?',
      a: `We tailor our ${service.title} specifically for the US market, ensuring compliance, local relevance, and results-driven strategies.`,
    },
    {
      q: 'How do I get started?',
      a: "Simply contact us or schedule a call. We'll discuss your needs and create a custom plan for your business.",
    },
  ];

  return (
    <div className="relative isolate min-h-screen overflow-hidden pt-16">
      <Helmet>
        <title>{serviceSeo(service).title}</title>
        <meta name="description" content={serviceSeo(service).description} />
        <link rel="canonical" href={`https://www.omnicraftservices.com/services/${service.id}`} />
        <link rel="icon" type="image/png" href="/logo.png" />
        <link rel="apple-touch-icon" href="/logo.png" />
      </Helmet>

      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[600px]">
        <div className="absolute left-[-10%] top-[-160px] h-[480px] w-[720px] max-w-full rounded-full bg-primary/25 blur-[120px]" />
        <div className="absolute right-[-80px] top-32 h-72 w-72 rounded-full bg-violet-500/20 blur-[100px]" />
      </div>

      {/* Hero */}
      <section className="container-custom pb-14 pt-10 md:pt-16">
        <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1.5 text-sm text-slate-600 backdrop-blur transition hover:border-primary/40 hover:text-primary dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300">
          <ArrowLeft size={14} /> All services
        </Link>

        <div className="mt-8 grid items-center gap-10 lg:grid-cols-2">
          <div>
            <div className="flex items-center gap-3">
              {IconComponent && (
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-primary text-white shadow-lg shadow-primary/30">
                  <IconComponent size={22} />
                </span>
              )}
              <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                {getCategoryLabel(service.category)}
              </span>
            </div>
            <h1 className="mt-5 text-4xl font-bold tracking-tight [text-wrap:balance] md:text-5xl">{service.title}</h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-slate-300">{service.description}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full shadow-lg shadow-primary/25">
                <Link to="/contact">Request a quote <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full">
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  <Calendar className="mr-2 h-4 w-4" /> Schedule a call
                </a>
              </Button>
            </div>
          </div>

          <div className="relative">
            <div aria-hidden className="absolute -inset-3 -z-10 rounded-[2rem] bg-gradient-to-br from-primary/30 to-violet-500/20 blur-2xl" />
            <img
              src={serviceImage(service.image, 1200)}
              alt={service.title}
              className="aspect-[4/3] w-full rounded-[2rem] border border-white/10 object-cover shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="container-custom py-14">
        <h2 className="text-3xl font-bold tracking-tight">What we offer</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {service.offerings.map((offering, index) => (
            <div
              key={offering.name}
              className="rounded-3xl border border-slate-200/80 bg-white/80 p-6 transition hover:-translate-y-0.5 hover:border-primary/40 dark:border-white/10 dark:bg-white/[0.03]"
            >
              <span className="font-mono text-xs text-primary">{String(index + 1).padStart(2, '0')}</span>
              <h3 className="mt-3 text-lg font-semibold tracking-tight">{offering.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{offering.description}</p>
            </div>
          ))}
        </div>

        {crossLink && (
          <div className="mt-8 flex flex-col gap-4 rounded-3xl border border-primary/30 bg-primary/5 p-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold">{crossLink.title}</h3>
              <p className="mt-1 text-slate-600 dark:text-slate-400">{crossLink.body}</p>
            </div>
            <Button asChild variant="secondary" className="shrink-0 rounded-full">
              <Link to={crossLink.to}>{crossLink.cta}</Link>
            </Button>
          </div>
        )}
      </section>

      {/* Benefits + skills */}
      <section className="container-custom grid gap-6 py-6 lg:grid-cols-[3fr_2fr]">
        <div className="rounded-3xl border border-slate-200/80 bg-slate-50/70 p-8 dark:border-white/10 dark:bg-white/[0.02]">
          <h2 className="text-2xl font-bold tracking-tight">What you get out of it</h2>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2">
            {service.benefits.map(benefit => (
              <li key={benefit} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span className="text-slate-700 dark:text-slate-300">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-3xl border border-slate-200/80 bg-slate-50/70 p-8 dark:border-white/10 dark:bg-white/[0.02]">
          <h2 className="text-2xl font-bold tracking-tight">Skills &amp; tools</h2>
          <ul className="mt-6 flex flex-wrap gap-2">
            {stack.map(skill => (
              <li key={skill} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300">
                {skill}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="container-custom py-14">
          <h2 className="text-3xl font-bold tracking-tight">Related services</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {related.map(s => <ServiceCard key={s.id} service={s} />)}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="container-custom grid gap-10 py-14 lg:grid-cols-[1fr_2fr]">
        <h2 className="text-3xl font-bold tracking-tight">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="rounded-3xl border border-slate-200/80 bg-white/80 px-6 dark:border-white/10 dark:bg-white/[0.03]">
          {faqs.map(({ q, a }, i) => (
            <AccordionItem key={q} value={`faq-${i}`} className={i === faqs.length - 1 ? 'border-b-0' : ''}>
              <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">{q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 dark:text-slate-400">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(({ q, a }) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
          })),
        }) }} />
      </section>

      {/* CTA */}
      <section className="container-custom pb-20">
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-primary via-blue-600 to-violet-600 px-6 py-12 text-white shadow-2xl shadow-primary/20 md:px-12">
          <div aria-hidden className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="max-w-xl">
              <h2 className="text-3xl font-bold tracking-tight">Ready to get started?</h2>
              <p className="mt-2 text-white/85">
                Tell us what you need from {service.title.toLowerCase()}. We will come back with a plan and a price.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" variant="secondary" className="rounded-full">
                <Link to="/contact">Contact us</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white hover:text-primary">
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  <Calendar className="mr-2 h-4 w-4" /> Schedule a call
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
